import React from 'react'
import { useSelector } from 'react-redux'
import { Grid } from '@mui/material'
import { styled } from '@mui/styles'
import { muiTheme } from '@pbt/pbt-ui-components'

import FeatureToggle from '~/constants/featureToggle'
import { getFeatureToggle } from '~/store/reducers/constants'
import type { QuestionAnswer, QuestionTimelineItemEntry } from '~/types'

import QuestionTitle from '../QuestionTitle'
import AnswerItem from './AnswerItem'
import { getListComponent, StyledUnBulletedList } from './cardUtils'

const StyledBulletedList = styled('ul')(({ theme }) => ({
  listStyleType: 'disc',
  paddingLeft: theme.spacing(3),
  margin: 0,
  '& li': {
    paddingLeft: 0,
    marginLeft: 0,
  },
  '& p': {
    display: 'inline',
  },
}))

const AnswerList = ({ entries }: { entries: QuestionTimelineItemEntry[] }) => {
  const isCVCIntakeQuestionnaireEnabled = useSelector(
    getFeatureToggle(FeatureToggle.CVC_INTAKE_QUESTIONNAIRE),
  )
  const isFollowUpQuestionsEnabled = useSelector(
    getFeatureToggle(FeatureToggle.FOLLOW_UP_QUESTIONS),
  )
  const StyledList = getListComponent(entries)

  const renderAnswerWithFollowUp = (
    answer: QuestionAnswer,
    entry: QuestionTimelineItemEntry,
    answerIndex: string,
  ) => {
    const { answerText, followUpQuestionLog } = answer

    const FollowUpStyledList = followUpQuestionLog
      ? getListComponent(followUpQuestionLog.entries || [])
      : StyledUnBulletedList

    return (
      <AnswerItem
        answerText={answerText}
        entry={entry}
        key={`${entry.id}-answer-${answerIndex}`}
      >
        {isFollowUpQuestionsEnabled && followUpQuestionLog?.entries && (
          <Grid container>
            <QuestionTitle
              questionColor="grayGray1"
              questionSubText={followUpQuestionLog.questionSubText}
              questionSubTextStyle={{
                fontSize: isCVCIntakeQuestionnaireEnabled
                  ? muiTheme.typography.body2.fontSize
                  : muiTheme.typography.body1.fontSize,
              }}
              questionText={followUpQuestionLog.questionText || ''}
              questionTextStyle={{
                fontSize: isCVCIntakeQuestionnaireEnabled
                  ? muiTheme.typography.body1.fontSize
                  : muiTheme.typography.h3.fontSize,
              }}
            />
            <FollowUpStyledList>
              {followUpQuestionLog.entries?.map((followUpEntry) => {
                if (followUpEntry.answers && followUpEntry.answers.length > 0) {
                  const AnswersStyledList =
                    followUpEntry.answers.length > 1
                      ? StyledBulletedList
                      : StyledUnBulletedList

                  return (
                    <li key={followUpEntry.id}>
                      <AnswersStyledList>
                        {followUpEntry.answers.map((followUpAnswer) => (
                          <AnswerItem
                            answerText={followUpAnswer.answerText}
                            entry={followUpEntry}
                            key={`${followUpEntry.id}-answer-${followUpAnswer.answerText}`}
                          />
                        ))}
                      </AnswersStyledList>
                    </li>
                  )
                }

                // TODO: remove after CVC_INTAKE_QUESTIONNAIRE will be deleted - Fallback to old structure
                return (
                  <AnswerItem entry={followUpEntry} key={followUpEntry.id} />
                )
              })}
            </FollowUpStyledList>
          </Grid>
        )}
      </AnswerItem>
    )
  }

  return (
    <StyledList>
      {entries.map((entry) => {
        if (entry.answers && entry.answers.length > 0) {
          const AnswersStyledList =
            entry.answers.length > 1 ? StyledBulletedList : StyledUnBulletedList

          return (
            <li key={entry.id}>
              <AnswersStyledList>
                {entry.answers.map((answer, index) =>
                  renderAnswerWithFollowUp(
                    answer,
                    entry,
                    `${entry.id}-answer-${index}`,
                  ),
                )}
              </AnswersStyledList>
            </li>
          )
        }

        // TODO: remove after CVC_INTAKE_QUESTIONNAIRE will be deleted - Old structure: Fallback to legacy answerText and followUpQuestionLog
        const { followUpQuestionLog } = entry

        const FollowUpStyledList = followUpQuestionLog
          ? getListComponent(followUpQuestionLog.entries || [])
          : StyledUnBulletedList

        return (
          <AnswerItem entry={entry} key={entry.id}>
            {isFollowUpQuestionsEnabled && followUpQuestionLog?.entries && (
              <Grid container>
                <QuestionTitle
                  questionColor="grayGray1"
                  questionSubText={followUpQuestionLog.questionSubText}
                  questionSubTextStyle={{
                    fontSize: isCVCIntakeQuestionnaireEnabled
                      ? muiTheme.typography.body2.fontSize
                      : muiTheme.typography.body1.fontSize,
                  }}
                  questionText={followUpQuestionLog.questionText || ''}
                  questionTextStyle={{
                    fontSize: isCVCIntakeQuestionnaireEnabled
                      ? muiTheme.typography.body1.fontSize
                      : muiTheme.typography.h3.fontSize,
                  }}
                />
                <FollowUpStyledList>
                  {followUpQuestionLog.entries?.map((followUpEntry) => (
                    <AnswerItem entry={followUpEntry} key={followUpEntry.id} />
                  ))}
                </FollowUpStyledList>
              </Grid>
            )}
          </AnswerItem>
        )
      })}
    </StyledList>
  )
}

export default AnswerList
